import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Upload,
  FileSpreadsheet,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  Loader,
  Download,
  Lock,
  Database,
  X,
  Sheet,
  Sparkles,
  RefreshCw,
  FolderOpen,
} from 'lucide-react';
import { GlassCard, GlassButton } from '../ui';
import {
  crmImportApi,
  CrmImportJob,
  CrmImportSheet,
  CRM_FIELD_OPTIONS,
  CRM_IMPORT_MODULES,
  ConfirmSheet,
  FULL_NAME_SENTINEL,
  GsheetKeyStrategy,
  GsheetSyncOptions,
} from '../../services/crmImportApi';
import { getCrmWritableProjects, Project } from '../../services/api';
import CrmImportCampaignPicker from './CrmImportCampaignPicker';
import GoogleSheetsSourcePicker from './GoogleSheetsSourcePicker';
import {
  GSHEETS_CALLBACK_ERRORS,
  GSHEETS_CALLBACK_FALLBACK,
  GsheetImportJob,
  GsheetSource,
} from '../../services/gsheetImportApi';

type Step = 'upload' | 'configure' | 'mapping' | 'progress';
type Source = 'file' | 'gsheet';

// A job handed in from outside the panel (google-sheets-lead-import Phase 3a):
// a /remap job to map, or a syncing source's latest job to show. `nonce`
// changes on every hand-off so the same job can be opened twice.
export interface ExternalJob {
  job: GsheetImportJob | CrmImportJob;
  step: 'configure' | 'progress';
  source?: GsheetSource | null;
  nonce: number;
}

interface Props {
  canImport: boolean;
  tierLabel: string;
  onToast: (message: string, type: 'success' | 'error' | 'info') => void;
  projectId?: string | null;
  externalJob?: ExternalJob | null;
  onSourcesChanged?: () => void;
}

// "Keep importing new rows" key select values: 'col:<header>' | 'email_created' | 'row_hash'.
const KEY_EMAIL = 'email_created';
const KEY_ROW = 'row_hash';
const INTERVALS: { value: number; label: string }[] = [
  { value: 15, label: 'Every 15 minutes' },
  { value: 30, label: 'Every 30 minutes' },
  { value: 60, label: 'Every hour' },
  { value: 180, label: 'Every 3 hours' },
  { value: 1440, label: 'Once a day' },
];

function keyChoiceToStrategy(choice: string): { key_strategy: GsheetKeyStrategy; key_column: string | null } {
  if (choice === KEY_EMAIL) return { key_strategy: 'email_created', key_column: null };
  if (choice === KEY_ROW) return { key_strategy: 'row_hash', key_column: null };
  return { key_strategy: 'column', key_column: choice.startsWith('col:') ? choice.slice(4) : null };
}

function sourceKeyChoice(src: GsheetSource): string {
  if (src.key_strategy === 'column') return `col:${src.key_column || ''}`;
  return src.key_strategy === 'email_created' ? KEY_EMAIL : KEY_ROW;
}

const SKIP = '__skip__';
const CONTACT_MODULES = ['Leads', 'Contacts'];

// lead_source options for a sheet: from a column already mapped to lead_source
// (its enum_options), else the suggest response's lead_source_options passthrough.
function leadSourceOptions(sheet: CrmImportSheet): Record<string, string> {
  const fromMapping = Object.values(sheet.suggested_mapping || {}).find(
    (m) => m?.field === 'lead_source' && m.enum_options && Object.keys(m.enum_options).length > 0,
  );
  return fromMapping?.enum_options || sheet.lead_source_options || {};
}

function guessModule(name: string, sourceKind?: string): string {
  // A Google Sheet tab is usually "Sheet1" and holds Meta lead-ad leads → Leads.
  if (sourceKind === 'gsheet') return 'Leads';
  const n = (name || '').toLowerCase();
  if (/account|compan|organis|organiz/.test(n)) return 'Accounts';
  if (/lead|prospect/.test(n)) return 'Leads';
  return 'Contacts';
}

const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  if (typeof detail === 'object' && typeof detail.detail === 'string') return detail.detail;
  return fallback;
};

const CrmImportPanel: React.FC<Props> = ({
  canImport, tierLabel, onToast, projectId, externalJob, onSourcesChanged,
}) => {
  const [step, setStep] = useState<Step>('upload');
  const [job, setJob] = useState<CrmImportJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [source, setSource] = useState<Source>('file');
  // crm-per-project-scoping: the project these records belong to (required at
  // confirm). Defaults to the AiMe selected project.
  const [importProjectId, setImportProjectId] = useState<string | null>(projectId || null);
  const [projectOptions, setProjectOptions] = useState<Project[]>([]);
  useEffect(() => {
    let cancelled = false;
    getCrmWritableProjects()
      .then((list) => { if (!cancelled) setProjectOptions(list); })
      .catch(() => { if (!cancelled) setProjectOptions([]); });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    // Follow the sidebar selection until the user picks a project here.
    if (step === 'upload') setImportProjectId(projectId || null);
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps


  // sheet_id -> chosen module (or SKIP)
  const [sheetModules, setSheetModules] = useState<Record<number, string>>({});
  // sheet_id -> { header -> field|'' }
  const [mappings, setMappings] = useState<Record<number, Record<string, string>>>({});
  // sheet_id -> per-sheet constants (Phase 2: { lead_source })
  const [staticValues, setStaticValues] = useState<Record<number, Record<string, string>>>({});
  // over-cap state surfaced from a 409 on confirm
  const [overCap, setOverCap] = useState<{ limit: number; total_rows: number } | null>(null);

  // ── Keep importing new rows (Google Sheet jobs only, Phase 3a) ──────────────
  const [syncOn, setSyncOn] = useState(false);
  const [syncInterval, setSyncInterval] = useState(60);
  const [keyChoice, setKeyChoice] = useState<string>(KEY_ROW);
  const [timeColumn, setTimeColumn] = useState<string>('');
  const [existingSourceId, setExistingSourceId] = useState<number | null>(null);
  const [linkedSource, setLinkedSource] = useState<GsheetSource | null>(null);
  const [keyError, setKeyError] = useState<string | null>(null);
  const [keyChange, setKeyChange] = useState<{ detail: string; recognised: number; known: number } | null>(null);

  // ── Google Sheets OAuth return (?gsheets=connected | ?gsheets_error=<code>) ──
  // Fixed messages per code — the raw query value is never rendered.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ok = params.get('gsheets');
    const err = params.get('gsheets_error');
    if (!ok && !err) return;
    if (ok === 'connected') onToast('Google Sheets connected', 'success');
    else if (err) onToast(GSHEETS_CALLBACK_ERRORS[err] || GSHEETS_CALLBACK_FALLBACK, 'error');
    setSource('gsheet');
    params.delete('gsheets');
    params.delete('gsheets_error');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Polling while a job is running ──────────────────────────────────────────
  useEffect(() => {
    if (!job || (job.status !== 'running' && job.status !== 'confirmed')) return;
    const t = setInterval(async () => {
      try {
        const { data } = await crmImportApi.getJob(job.id);
        setJob(data);
        if (data.status === 'done' || data.status === 'failed') clearInterval(t);
      } catch {
        /* keep polling */
      }
    }, 2500);
    return () => clearInterval(t);
  }, [job?.id, job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => {
    setStep('upload');
    setJob(null);
    setSheetModules({});
    setMappings({});
    setStaticValues({});
    setOverCap(null);
    setCampaignId(null);
    setImportProjectId(projectId || null);
    setSyncOn(false);
    setSyncInterval(60);
    setExistingSourceId(null);
    setLinkedSource(null);
    setKeyError(null);
    setKeyChange(null);
  };

  // ── A staged job (file upload or Google Sheet tab) → configure step ──────────
  const startJob = useCallback((data: GsheetImportJob | CrmImportJob, source?: GsheetSource | null) => {
    setJob(data);
    const defaults: Record<number, string> = {};
    data.sheets.forEach((s) => { defaults[s.sheet_id] = guessModule(s.sheet_name, data.source_kind); });
    if (source && CONTACT_MODULES.includes(source.target_module)) {
      data.sheets.forEach((s) => { defaults[s.sheet_id] = source.target_module; });
    }
    setSheetModules(defaults);
    setOverCap(null);
    setKeyError(null);
    setKeyChange(null);
    // Keep-syncing defaults (gsheet only).
    const headers = data.sheets[0]?.headers || [];
    const linked = data.gsheet_source_id != null ? (source || null) : null;
    setLinkedSource(linked);
    setExistingSourceId(('existing_source_id' in data ? data.existing_source_id : null) ?? null);
    if (linked) {
      setSyncOn(true);
      setSyncInterval(linked.sync_interval_minutes || 60);
      const stored = sourceKeyChoice(linked);
      const keyMissing = linked.key_strategy === 'column' && !headers.includes(linked.key_column || '');
      setKeyChoice(keyMissing ? `col:${headers.find((h) => h !== linked.key_column) || ''}` : stored);
      const timeMissing = !!linked.key_time_column && !headers.includes(linked.key_time_column);
      setTimeColumn(timeMissing ? '' : (linked.key_time_column || ''));
      setCampaignId(linked.campaign_id);
      if (linked.project_id) setImportProjectId(linked.project_id);
    } else {
      setSyncOn(data.gsheet_source_id != null);
      setSyncInterval(60);
      setKeyChoice(headers.includes('id') ? 'col:id' : KEY_ROW);
      setTimeColumn(headers.includes('created_time') ? 'created_time' : '');
    }
    setStep('configure');
  }, []);

  // A job handed in by the syncing-sheets list (re-map, or latest import).
  useEffect(() => {
    if (!externalJob) return;
    if (externalJob.step === 'progress') {
      setJob(externalJob.job);
      setStep('progress');
      return;
    }
    startJob(externalJob.job, externalJob.source);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalJob?.nonce]);

  // ── Upload ──────────────────────────────────────────────────────────────────
  const handleFile = useCallback(async (file: File) => {
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'csv' && ext !== 'xlsx') {
      onToast('Please choose a .csv or .xlsx file', 'error');
      return;
    }
    setBusy(true);
    try {
      const { data } = await crmImportApi.upload(file);
      startJob(data);
    } catch (e: any) {
      onToast(getApiError(e, 'Upload failed'), 'error');
    } finally {
      setBusy(false);
    }
  }, [onToast, startJob]);

  // ── Generate mapping (suggest) ──────────────────────────────────────────────
  const generateMapping = async () => {
    if (!job) return;
    setBusy(true);
    try {
      const sheetTargets = job.sheets.map((s) => ({
        sheet_id: s.sheet_id,
        target_module: sheetModules[s.sheet_id] === SKIP ? null : sheetModules[s.sheet_id],
        account_link: { strategy: 'auto' },
      }));
      const { data } = await crmImportApi.suggest(job.id, sheetTargets);
      setJob(data);
      // seed editable mappings from suggestions
      const seed: Record<number, Record<string, string>> = {};
      data.sheets.forEach((s) => {
        if (!s.target_module) return;
        const m: Record<string, string> = {};
        (s.headers || []).forEach((h) => {
          m[h] = s.suggested_mapping?.[h]?.field ?? '';
        });
        seed[s.sheet_id] = m;
      });
      setMappings(seed);
      const sv: Record<number, Record<string, string>> = {};
      data.sheets.forEach((s) => { sv[s.sheet_id] = { ...(s.static_values || {}) }; });
      setStaticValues(sv);
      setStep('mapping');
    } catch (e: any) {
      onToast(getApiError(e, 'Could not generate mapping'), 'error');
    } finally {
      setBusy(false);
    }
  };

  // ── Confirm / start import ──────────────────────────────────────────────────
  const activeSheets = (job?.sheets || []).filter((s) => sheetModules[s.sheet_id] && sheetModules[s.sheet_id] !== SKIP);

  const missingRequired = (): string | null => {
    for (const s of activeSheets) {
      const mod = sheetModules[s.sheet_id];
      const required = (CRM_FIELD_OPTIONS[mod] || []).filter((f) => f.required).map((f) => f.field);
      const mapped = new Set(Object.values(mappings[s.sheet_id] || {}));
      // A full-name column is split into first/last, so it satisfies Last Name (same rule as the backend).
      if (mapped.has(FULL_NAME_SENTINEL)) mapped.add('last_name');
      for (const req of required) {
        if (!mapped.has(req)) {
          const label = (CRM_FIELD_OPTIONS[mod] || []).find((f) => f.field === req)?.label || req;
          return `Sheet "${s.sheet_name}" (${mod}) needs a column mapped to "${label}"`;
        }
      }
    }
    return null;
  };

  const isGsheet = job?.source_kind === 'gsheet';
  const isLinked = job?.gsheet_source_id != null;
  const syncHeaders = activeSheets[0]?.headers || job?.sheets[0]?.headers || [];
  const emailMapped = activeSheets.some((s) => Object.values(mappings[s.sheet_id] || {}).includes('email1'));

  const buildSync = (): GsheetSyncOptions | undefined => {
    if (!isGsheet || !syncOn) return undefined;
    const { key_strategy, key_column } = keyChoiceToStrategy(keyChoice);
    return {
      enabled: true,
      interval_minutes: syncInterval,
      key_strategy,
      key_column,
      key_time_column: timeColumn || null,
    };
  };

  const doConfirm = async (allowPartial: boolean) => {
    if (!job) return;
    const miss = missingRequired();
    if (miss) { onToast(miss, 'error'); return; }
    const gsheetSync = buildSync();
    if (gsheetSync && gsheetSync.key_strategy === 'column' && !gsheetSync.key_column) {
      setKeyError('Pick the column that identifies each lead');
      return;
    }
    if (gsheetSync && gsheetSync.key_strategy === 'email_created' && !gsheetSync.key_time_column) {
      setKeyError('Pick the time column used together with the email');
      return;
    }
    setBusy(true);
    setOverCap(null);
    setKeyError(null);
    setKeyChange(null);
    try {
      const sheets: ConfirmSheet[] = job.sheets.map((s) => {
        const mod = sheetModules[s.sheet_id];
        const active = mod && mod !== SKIP;
        const confirmed_mapping: Record<string, string | null> = {};
        if (active) {
          Object.entries(mappings[s.sheet_id] || {}).forEach(([h, f]) => {
            confirmed_mapping[h] = f || null;
          });
        }
        const out: ConfirmSheet = {
          sheet_id: s.sheet_id,
          target_module: active ? mod : null,
          confirmed_mapping,
          account_link: { strategy: 'auto' },
        };
        // Constants only for Leads/Contacts; other sheets omit the key so the
        // server re-validates (and drops) any held value against the final module.
        if (active && CONTACT_MODULES.includes(mod)) {
          const sv: Record<string, string> = {};
          Object.entries(staticValues[s.sheet_id] || {}).forEach(([k, v]) => { if (v) sv[k] = v; });
          out.static_values = sv;
        }
        return out;
      });
      const { data: res } = await crmImportApi.confirm(
        job.id, sheets, allowPartial, campaignId, gsheetSync, importProjectId,
      );
      if (gsheetSync) onSourcesChanged?.();
      if (res.queued === false) {
        onToast('No new rows to import — the sheet will keep syncing', 'success');
        reset();
        return;
      }
      if (res.key_conflicts && res.key_conflicts > 0) {
        onToast(`${res.key_conflicts} rows share a key and were not imported — see Syncing sheets`, 'info');
      }
      const { data } = await crmImportApi.getJob(job.id);
      setJob(data);
      setStep('progress');
      onToast(gsheetSync ? 'Import started — new rows will keep syncing' : 'Import started', 'success');
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const code = detail && typeof detail === 'object' ? detail.error : null;
      if (code === 'over_cap') {
        setOverCap({ limit: detail.limit, total_rows: detail.total_rows });
      } else if (code === 'key_change_unsupported') {
        setKeyChange({ detail: detail.detail, recognised: detail.recognised, known: detail.known });
      } else if (code === 'key_not_unique') {
        setKeyError(detail.detail);
      } else if (code === 'source_exists') {
        setExistingSourceId(detail.source_id ?? null);
        setSyncOn(false);
        onToast('This tab already syncs. Manage it under Syncing sheets below', 'info');
      } else {
        onToast(getApiError(e, 'Could not start import'), 'error');
      }
    } finally {
      setBusy(false);
    }
  };

  // ── Error report download ───────────────────────────────────────────────────
  const downloadErrors = async () => {
    if (!job) return;
    try {
      const { data } = await crmImportApi.downloadErrors(job.id);
      const blob = new Blob([data as any], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm_import_${job.id}_errors.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      onToast('Could not download error report', 'error');
    }
  };

  // ── Render: tier-locked ─────────────────────────────────────────────────────
  if (!canImport) {
    return (
      <GlassCard className="crm-import-panel">
        <div className="crm-import-locked">
          <Lock size={28} />
          <h3>CRM Data Import</h3>
          <p>Importing your existing CRM data is available on the Growth plan and above.</p>
          <span className="crm-import-tier-badge">{tierLabel}</span>
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard className="crm-import-panel">
      <div className="crm-import-header">
        <div className="crm-import-title">
          <Database size={20} />
          <div>
            <h3>CRM Data Import</h3>
            <p>Bring your contacts, leads and companies in from a CSV or Excel export, or a Google Sheet.</p>
          </div>
        </div>
        {job && step !== 'upload' && (
          <GlassButton variant="secondary" size="sm" onClick={reset} disabled={busy}>
            <X size={15} /> Start over
          </GlassButton>
        )}
      </div>

      {/* Stepper */}
      <div className="crm-import-steps">
        {(['upload', 'configure', 'mapping', 'progress'] as Step[]).map((s, i) => (
          <div key={s} className={`crm-import-step ${step === s ? 'is-active' : ''} ${stepIndex(step) > i ? 'is-done' : ''}`}>
            <span className="crm-import-step__num">{stepIndex(step) > i ? <CheckCircle size={14} /> : i + 1}</span>
            <span className="crm-import-step__label">{stepLabel(s)}</span>
          </div>
        ))}
      </div>

      {/* ── Step: Upload — source toggle ── */}
      {step === 'upload' && (
        <div className="crm-import-source-toggle" role="tablist" aria-label="Import source">
          <button
            type="button"
            role="tab"
            aria-selected={source === 'file'}
            className={`crm-import-source-toggle__btn ${source === 'file' ? 'is-active' : ''}`}
            onClick={() => setSource('file')}
            disabled={busy}
          >
            <Upload size={14} /> Upload file
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={source === 'gsheet'}
            className={`crm-import-source-toggle__btn ${source === 'gsheet' ? 'is-active' : ''}`}
            onClick={() => setSource('gsheet')}
            disabled={busy}
          >
            <Sheet size={14} /> Google Sheet
          </button>
        </div>
      )}

      {step === 'upload' && source === 'gsheet' && (
        <GoogleSheetsSourcePicker onJob={startJob} onToast={onToast} />
      )}

      {/* ── Step: Upload ── */}
      {step === 'upload' && source === 'file' && (
        <div
          className={`crm-import-drop ${dragOver ? 'is-drag' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]); }}
          onClick={() => fileRef.current?.click()}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx"
            style={{ display: 'none' }}
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          />
          {busy ? <Loader size={28} className="crm-spin" /> : <Upload size={28} />}
          <p className="crm-import-drop__main">{busy ? 'Parsing file…' : 'Drop a CSV or Excel file here, or click to browse'}</p>
          <p className="crm-import-drop__sub">{tierLabel} · .csv and .xlsx (multi-sheet) supported</p>
        </div>
      )}

      {/* ── Step: Configure (per-sheet module) ── */}
      {step === 'configure' && job && (
        <div className="crm-import-configure">
          <p className="crm-import-hint">Choose what each sheet should become in your CRM. Skip anything you don't want to import.</p>
          {job.sheets.map((s) => (
            <div key={s.sheet_id} className="crm-import-sheet-row">
              <div className="crm-import-sheet-row__info">
                <FileSpreadsheet size={16} />
                <span className="crm-import-sheet-row__name">{s.sheet_name}</span>
                <span className="crm-import-sheet-row__count">{s.row_count} rows · {s.headers.length} columns</span>
              </div>
              <select
                className="crm-import-select"
                value={sheetModules[s.sheet_id] || SKIP}
                onChange={(e) => setSheetModules((m) => ({ ...m, [s.sheet_id]: e.target.value }))}
              >
                {CRM_IMPORT_MODULES.map((mod) => <option key={mod} value={mod}>{mod}</option>)}
                <option value={SKIP}>— Skip this sheet —</option>
              </select>
            </div>
          ))}
          <div className="crm-import-actions">
            <GlassButton variant="primary" onClick={generateMapping} disabled={busy || activeSheets.length === 0}>
              {busy ? <Loader size={15} className="crm-spin" /> : <ArrowRight size={15} />} Generate field mapping
            </GlassButton>
          </div>
        </div>
      )}

      {/* ── Step: Mapping review ── */}
      {step === 'mapping' && job && (
        <div className="crm-import-mapping">
          <p className="crm-import-hint">
            We've suggested how each column maps to your CRM. Review and adjust, then start the import.
            Unmatched values on dropdown fields are flagged — they'll import as-is.
          </p>
          {activeSheets.map((s) => {
            const mod = sheetModules[s.sheet_id];
            const isContact = CONTACT_MODULES.includes(mod);
            const lsOptions = isContact ? leadSourceOptions(s) : {};
            const lsMapped = Object.values(mappings[s.sheet_id] || {}).includes('lead_source');
            return (
              <React.Fragment key={s.sheet_id}>
                {s.detected_preset === 'meta_lead_ads' && isContact && (
                  <div className="crm-import-preset-banner">
                    <Sparkles size={16} />
                    <span>Meta Lead Ads columns detected — mapped automatically. The campaign, ad and form are recorded as lead attribution.</span>
                  </div>
                )}
                <MappingTable
                  sheet={s}
                  module={mod}
                  mapping={mappings[s.sheet_id] || {}}
                  onChange={(header, field) =>
                    setMappings((all) => ({ ...all, [s.sheet_id]: { ...all[s.sheet_id], [header]: field } }))
                  }
                />
                {isContact && Object.keys(lsOptions).length > 0 && (
                  <div className="crm-import-sheet-row crm-import-static-row">
                    <div className="crm-import-sheet-row__info">
                      <span className="crm-import-sheet-row__name">Lead source for every row</span>
                      <span className="crm-import-sheet-row__count">
                        {lsMapped ? 'Used when the mapped Lead Source column is empty' : s.sheet_name}
                      </span>
                    </div>
                    <select
                      className="crm-import-select"
                      aria-label={`Lead source for every row of ${s.sheet_name}`}
                      value={staticValues[s.sheet_id]?.lead_source ?? ''}
                      onChange={(e) =>
                        setStaticValues((all) => ({
                          ...all,
                          [s.sheet_id]: { ...(all[s.sheet_id] || {}), lead_source: e.target.value },
                        }))
                      }
                    >
                      <option value="">— None —</option>
                      {Object.entries(lsOptions).map(([key, label]) => (
                        <option key={key} value={key}>{label || key}</option>
                      ))}
                    </select>
                  </div>
                )}
              </React.Fragment>
            );
          })}

          <div>
            <p className="crm-import-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <FolderOpen size={15} />
              Project these records belong to <span style={{ color: 'var(--text-secondary)' }}>(required)</span>
            </p>
            <div className="crm-import-actions" style={{ justifyContent: 'flex-start' }}>
              <select
                className="crm-import-select"
                value={importProjectId || ''}
                aria-label="Project"
                onChange={(e) => {
                  setImportProjectId(e.target.value || null);
                  setCampaignId(null);   // the campaign must belong to the chosen project
                }}
              >
                <option value="">— Choose a project —</option>
                {projectOptions.map((p) => (
                  <option key={p.project_id} value={p.project_id}>{p.name}</option>
                ))}
              </select>
            </div>
            <p className="crm-import-hint">
              Imported records go into this project's CRM. People the project is shared with can see them.
            </p>
          </div>

          <CrmImportCampaignPicker
            projectId={importProjectId}
            campaignId={campaignId}
            onChange={setCampaignId}
          />

          {isGsheet && (
            <SyncControls
              headers={syncHeaders}
              syncOn={syncOn}
              setSyncOn={setSyncOn}
              interval={syncInterval}
              setInterval={setSyncInterval}
              keyChoice={keyChoice}
              setKeyChoice={(v) => { setKeyChoice(v); setKeyError(null); setKeyChange(null); }}
              timeColumn={timeColumn}
              setTimeColumn={(v) => { setTimeColumn(v); setKeyError(null); setKeyChange(null); }}
              emailMapped={emailMapped}
              existingSourceId={isLinked ? null : existingSourceId}
              linkedSource={isLinked ? linkedSource : null}
              isLinked={isLinked}
              campaignTagged={!!campaignId}
              keyError={keyError}
              keyChange={keyChange}
            />
          )}

          {overCap && (
            <div className="crm-import-overcap">
              <AlertTriangle size={18} />
              <div className="crm-import-overcap__body">
                <strong>This file has {overCap.total_rows} contacts, above your plan limit of {overCap.limit}.</strong>
                <div className="crm-import-overcap__actions">
                  <GlassButton variant="primary" size="sm" onClick={() => { window.location.href = '/profile?upgrade=1'; }}>
                    Upgrade plan
                  </GlassButton>
                  {overCap.limit > 0 && (
                    <GlassButton variant="secondary" size="sm" onClick={() => doConfirm(true)} disabled={busy || !importProjectId}>
                      Import first {overCap.limit} anyway
                    </GlassButton>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="crm-import-actions">
            <GlassButton variant="secondary" onClick={() => setStep('configure')} disabled={busy}>
              <ArrowLeft size={15} /> Back
            </GlassButton>
            <GlassButton variant="primary" onClick={() => doConfirm(false)} disabled={busy || !importProjectId}>
              {busy ? <Loader size={15} className="crm-spin" /> : <ArrowRight size={15} />} Start import
            </GlassButton>
          </div>
        </div>
      )}

      {/* ── Step: Progress ── */}
      {step === 'progress' && job && (
        <div className="crm-import-progress">
          <div className="crm-import-progress__status">
            {job.status === 'running' || job.status === 'confirmed' ? (
              <><Loader size={18} className="crm-spin" /> Importing…</>
            ) : job.status === 'done' ? (
              <><CheckCircle size={18} /> Import complete</>
            ) : (
              <><AlertTriangle size={18} /> Import failed</>
            )}
          </div>
          {job.total_rows > 0 && (
            <div className="crm-import-bar">
              <div
                className="crm-import-bar__fill"
                style={{ width: `${Math.min(100, Math.round(((job.records_processed + job.records_failed) / Math.max(1, job.total_rows)) * 100))}%` }}
              />
            </div>
          )}
          <div className="crm-import-stats">
            <Stat label="Created" value={job.records_created} />
            <Stat label="Updated" value={job.records_updated} />
            <Stat label="Accounts" value={job.accounts_created} />
            <Stat label="Failed" value={job.records_failed} warn={job.records_failed > 0} />
            {job.partial_over_cap && <Stat label="Skipped (over cap)" value={job.skipped_over_cap} warn />}
            {job.campaign_id && (
              <>
                <Stat label="Enrolled" value={job.enrollments_created} />
                <Stat label="Enroll failed" value={job.enrollments_failed} warn={job.enrollments_failed > 0} />
              </>
            )}
          </div>
          {job.campaign_id && (
            <p className="crm-import-hint">
              Tagged to campaign{job.campaign_name ? `: ${job.campaign_name}` : ''}. Imported contacts are being
              enrolled into that campaign's active email sequence(s).
            </p>
          )}
          {job.records_failed > 0 && (
            <GlassButton variant="secondary" size="sm" onClick={downloadErrors}>
              <Download size={15} /> Download error report
            </GlassButton>
          )}
          {(job.status === 'done' || job.status === 'failed') && (
            <div className="crm-import-actions">
              <GlassButton variant="primary" onClick={reset}>
              {job.source_kind === 'gsheet' ? 'Import another sheet' : 'Import another file'}
            </GlassButton>
            </div>
          )}
        </div>
      )}
    </GlassCard>
  );
};

// ── Keep importing new rows (Google Sheet jobs, Phase 3a) ─────────────────────
const SyncControls: React.FC<{
  headers: string[];
  syncOn: boolean;
  setSyncOn: (v: boolean) => void;
  interval: number;
  setInterval: (v: number) => void;
  keyChoice: string;
  setKeyChoice: (v: string) => void;
  timeColumn: string;
  setTimeColumn: (v: string) => void;
  emailMapped: boolean;
  existingSourceId: number | null;
  linkedSource: GsheetSource | null;
  isLinked: boolean;
  campaignTagged: boolean;
  keyError: string | null;
  keyChange: { detail: string; recognised: number; known: number } | null;
}> = (p) => {
  const blocked = p.existingSourceId != null;
  const src = p.linkedSource;
  // Re-map restriction (3a): keep the source's own key; a key/time column that
  // is missing from the headers may be re-pointed at another header.
  const keyMissing = !!src && src.key_strategy === 'column' && !p.headers.includes(src.key_column || '');
  const timeMissing = !!src && !!src.key_time_column && !p.headers.includes(src.key_time_column);
  let keyOptions: { value: string; label: string; disabled?: boolean }[];
  if (src && !keyMissing) {
    keyOptions = [{ value: sourceKeyChoice(src), label:
      src.key_strategy === 'column' ? `Column: ${src.key_column}`
        : src.key_strategy === 'email_created' ? 'Email + time column' : 'Whole row' }];
  } else if (src && keyMissing) {
    keyOptions = p.headers.filter((h) => h !== src.key_column).map((h) => ({ value: `col:${h}`, label: `Column: ${h}` }));
  } else {
    keyOptions = [
      ...p.headers.map((h) => ({ value: `col:${h}`, label: `Column: ${h}${h === 'id' ? ' (Meta lead id)' : ''}` })),
      { value: KEY_EMAIL, label: 'Email + time column', disabled: !p.emailMapped },
      { value: KEY_ROW, label: 'Whole row' },
    ];
  }
  const timeLocked = !!src && !timeMissing;
  const strategy = keyChoiceToStrategy(p.keyChoice).key_strategy;
  return (
    <div className="crm-import-sync">
      <label className="crm-import-sync__toggle">
        <input
          type="checkbox"
          checked={p.syncOn && !blocked}
          disabled={blocked || p.isLinked}
          onChange={(e) => p.setSyncOn(e.target.checked)}
        />
        <RefreshCw size={15} />
        <span>Keep importing new rows from this Sheet</span>
      </label>
      {blocked && (
        <p className="crm-import-sync__note">This tab already syncs. Manage it under Syncing sheets below.</p>
      )}
      {p.syncOn && !blocked && (
        <>
          <div className="crm-import-sync__row">
            <span className="crm-import-sync__label">How often</span>
            <select className="crm-import-select" value={p.interval}
              onChange={(e) => p.setInterval(Number(e.target.value))} aria-label="Sync interval">
              {INTERVALS.map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
            </select>
          </div>
          <div className="crm-import-sync__row">
            <span className="crm-import-sync__label">Recognise each lead by</span>
            <select className="crm-import-select" value={p.keyChoice} aria-label="Row key"
              onChange={(e) => p.setKeyChoice(e.target.value)} disabled={keyOptions.length <= 1 && !!src}>
              {keyOptions.map((o) => (
                <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>
              ))}
            </select>
          </div>
          {keyMissing && (
            <p className="crm-import-sync__note">
              Pick the renamed key column. If it holds the same values as before, nothing is re-imported.
            </p>
          )}
          {p.keyError && <p className="crm-import-inline-error"><AlertTriangle size={14} /> {p.keyError}</p>}
          {(strategy === 'email_created' || strategy === 'column') && (
            <div className="crm-import-sync__row">
              <span className="crm-import-sync__label">
                {strategy === 'email_created' ? 'Time column' : 'Time column (used for rows with a blank key)'}
              </span>
              <select className="crm-import-select" value={p.timeColumn} aria-label="Time column"
                onChange={(e) => p.setTimeColumn(e.target.value)} disabled={timeLocked}>
                {strategy !== 'email_created' && <option value="">— None —</option>}
                {strategy === 'email_created' && !p.timeColumn && <option value="">— Choose —</option>}
                {(timeMissing ? p.headers.filter((h) => h !== src?.key_time_column) : p.headers).map((h) => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
            </div>
          )}
          {timeMissing && (
            <p className="crm-import-sync__note">Pick the renamed time column. Same values as before → nothing is re-imported.</p>
          )}
          {strategy === 'row_hash' && !src && (
            <p className="crm-import-sync__note">
              Whole-row matching re-imports a row whenever one of its mapped cells is edited. A lead id column is
              more reliable when the sheet has one.
            </p>
          )}
          {p.keyChange && (
            <div className="crm-import-overcap">
              <AlertTriangle size={18} />
              <div className="crm-import-overcap__body">
                <strong>These key settings would re-import rows already in your CRM.</strong>
                <span>{p.keyChange.detail}</span>
                {p.keyChange.known > 0 && (
                  <span>Recognised {p.keyChange.recognised} of {p.keyChange.known} rows already imported.</span>
                )}
              </div>
            </div>
          )}
          {p.campaignTagged && (
            <p className="crm-import-sync__note">
              Rows added later are enrolled into this campaign's active 'On Lead Created' workflows too.
            </p>
          )}
          {!p.isLinked && (
            <p className="crm-import-sync__note">
              If you imported this tab before: rows already in your CRM are matched by email and updated, not
              duplicated; rows without an email are imported again.
            </p>
          )}
        </>
      )}
    </div>
  );
};

// ── Mapping table for one sheet ───────────────────────────────────────────────
const MappingTable: React.FC<{
  sheet: CrmImportSheet;
  module: string;
  mapping: Record<string, string>;
  onChange: (header: string, field: string) => void;
}> = ({ sheet, module, mapping, onChange }) => {
  const options = CRM_FIELD_OPTIONS[module] || [];
  const sample = sheet.sample_rows?.[0] || {};
  return (
    <div className="crm-map-table">
      <div className="crm-map-table__head">
        <FileSpreadsheet size={15} /> {sheet.sheet_name} <span className="crm-map-table__mod">→ {module}</span>
      </div>
      {sheet.headers.map((h) => {
        const sug = sheet.suggested_mapping?.[h];
        const unmatched = sug?.is_enum && sug.value_validation
          ? Object.entries(sug.value_validation).filter(([, v]) => v === null).map(([k]) => k)
          : [];
        return (
          <div key={h} className="crm-map-row">
            <div className="crm-map-row__source">
              <span className="crm-map-row__header">{h}</span>
              <span className="crm-map-row__sample">{String(sample[h] ?? '').slice(0, 32)}</span>
            </div>
            <ArrowRight size={13} className="crm-map-row__arrow" />
            <div className="crm-map-row__target">
              <select className="crm-import-select" value={mapping[h] ?? ''} onChange={(e) => onChange(h, e.target.value)}>
                <option value="">— Don't import —</option>
                {options.map((o) => <option key={o.field} value={o.field}>{o.label}{o.required ? ' *' : ''}</option>)}
              </select>
              {sug && sug.field && typeof sug.confidence === 'number' && (
                <span className={`crm-map-conf ${sug.confidence >= 0.85 ? 'is-high' : sug.confidence >= 0.6 ? 'is-mid' : 'is-low'}`}>
                  {Math.round(sug.confidence * 100)}%
                </span>
              )}
              {unmatched.length > 0 && (
                <span className="crm-map-warn" title={`Unmatched values: ${unmatched.join(', ')}`}>
                  <AlertTriangle size={13} /> {unmatched.length}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: number; warn?: boolean }> = ({ label, value, warn }) => (
  <div className={`crm-import-stat ${warn ? 'is-warn' : ''}`}>
    <span className="crm-import-stat__value">{value}</span>
    <span className="crm-import-stat__label">{label}</span>
  </div>
);

function stepIndex(step: Step): number {
  return ['upload', 'configure', 'mapping', 'progress'].indexOf(step);
}
function stepLabel(step: Step): string {
  return { upload: 'Upload', configure: 'Configure', mapping: 'Map fields', progress: 'Import' }[step];
}

export default CrmImportPanel;
